import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  Button,
  Checkbox,
  Dialog,
  Form,
  Input,
  Text,
  TextButton,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import { BrandLogo, InputLabel } from '~/components/common';

import { useAuthState, usePostUserLogin } from '~/hooks/auth';
import { authDBService } from '~/services';
import { validateIdFormat, validateNoSpace } from '~/utils';

import { EXTERNAL_URLS, LOGIN, PATH, TOAST_MESSAGE } from '~/constants';

import type { TypeInputState, TypeInputStatus } from '~/types/common.types';

import styles from './LoginPage.module.scss';

const LoginPage = () => {
  const { requestUserLogin } = usePostUserLogin();
  const navigate = useNavigate();

  const { updateIsAuthenticated } = useAuthState();

  const [loginId, setLoginId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [loginIdInputState, setLoginIdInputState] = useState<TypeInputState>({
    status: 'default',
  });
  const [loginPasswordInputState, setLoginPasswordInputState] = useState<TypeInputState>({
    status: 'default',
  });

  const [isRememberIdChecked, setIsRememberIdChecked] = useState(false);

  useEffect(() => {
    const initLoginInfo = async () => {
      const loginInfo = await authDBService.getLoginInfo();

      if (loginInfo) {
        const { login_id: savedLoginId, is_remember_id: savedIsRememberId } = loginInfo;

        setLoginId(savedLoginId ?? '');
        setIsRememberIdChecked(savedIsRememberId ?? false);
      }
    };

    initLoginInfo();
  }, []);

  const [openDuplicatedLoginDialog, setOpenDuplicatedLoginDialog] = useState(false);
  const [changePasswordDialogState, setChangePasswordDialogState] = useState<{
    open: boolean;
    content?: string;
  }>({ open: false });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isAllFieldsFilled = loginId && loginPassword;

    if (!isAllFieldsFilled) {
      validateEmptyFields();

      return;
    }

    const isLoginIdValid = validateNoSpace(loginId) && validateIdFormat(loginId) && loginId.length <= 100;
    const isLoginPasswordValid = validateNoSpace(loginPassword);

    if (!(isLoginIdValid && isLoginPasswordValid)) {
      setAllFieldsStatusAtOnce('error');
      toastService.addToast({
        type: 'error',
        message: '아이디 또는 비밀번호를 확인해 주세요.',
      });

      return;
    }

    postLogin();
  };

  const postLogin = async (forceLoginFlag?: boolean) => {
    setAllFieldsStatusAtOnce('default');

    requestUserLogin(
      {
        loginId,
        password: loginPassword,
        forceLoginFlag,
      },
      {
        onSuccess: async (data) => {
          const { expiredChangeDateLogin, initializationLogin, requireTwoFactor, twoFactorId, token } = data;

          if (initializationLogin) {
            await authDBService.saveChangePasswordInfo({
              login_id: loginId,
            });

            setChangePasswordDialogState({
              open: true,
              content: '최초 로그인 시 비밀번호 변경이 필요합니다.',
            });

            return;
          }

          if (expiredChangeDateLogin) {
            await authDBService.saveChangePasswordInfo({
              login_id: loginId,
            });

            setChangePasswordDialogState({
              open: true,
              content:
                '안전한 시스템 이용을 위해 기존 비밀번호를 변경해주세요. 비밀번호는 최소한 90일 주기로 변경하여야 합니다.',
            });

            return;
          }

          if (requireTwoFactor) {
            await authDBService.saveTwoFactorAuthInfo({
              login_id: loginId,
              two_factor_id: twoFactorId,
              is_remember_id: isRememberIdChecked,
            });

            navigate(PATH.TWO_FACTOR_AUTH);

            return;
          }

          await authDBService.saveLoginInfo({
            login_id: loginId,
            is_remember_id: isRememberIdChecked,
            access_token: token,
          });

          toastService.addToast({
            message: '정상적으로 로그인되었습니다.',
          });

          await updateIsAuthenticated();

          navigate(PATH.PRODUCT_REGISTER);
        },
        onError: (error) => {
          if (error?.code === LOGIN.DUPLICATED) {
            setOpenDuplicatedLoginDialog(true);
            return;
          }

          if (error?.code === LOGIN.PASSWORD_FAIL_UNDER_5_TIMES) {
            dialogService.addDialog({ title: '비밀번호를 다시 입력해주세요.', children: error?.message });
            return;
          }

          if (error?.code === LOGIN.PASSWORD_FAIL_OVER_5_TIMES) {
            dialogService.addDialog({
              type: 'confirm',
              title: '비밀번호를 초기화해주세요.',
              children:
                '로그인을 5회 실패하여 기존 비밀번호는\n사용할 수 없습니다.\n비밀번호 초기화 후 다시 시도해 주세요.',
              confirmText: '비밀번호 초기화',
              onConfirm: () => navigate(PATH.FIND_PASSWORD),
            });
            return;
          }

          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
          setAllFieldsStatusAtOnce('error');
        },
      }
    );
  };

  const handleClickCloseFirstLoginDialog = () => {
    setChangePasswordDialogState({ open: false });
    navigate(PATH.CHANGE_PASSWORD);
  };

  const setAllFieldsStatusAtOnce = (status: TypeInputStatus) => {
    setLoginIdInputState({ status });
    setLoginPasswordInputState({ status });
  };

  const validateEmptyFields = () => {
    if (!loginId) {
      setLoginIdInputState({
        status: 'error',
        message: '아이디를 입력해 주세요',
      });
    }

    if (!loginPassword) {
      setLoginPasswordInputState({
        status: 'error',
        message: '비밀번호를 입력해 주세요',
      });
    }
  };

  const handleOpenPrivacyPolicy = () => {
    window.open(EXTERNAL_URLS.PRIVACY_POLICY, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles['login-page']}>
      <div className={styles['content-area']}>
        <div className={styles['login-box']}>
          <BrandLogo className={styles['login-logo']} width={231} height={30} />
          <Form onSubmit={handleSubmit}>
            <div className={styles['input-label']}>
              <InputLabel name="아이디" htmlFor="login-id">
                <Input
                  id="login-id"
                  placeholder="아이디를 입력해 주세요."
                  type="text"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  status={loginIdInputState.status}
                  validationMessage={loginIdInputState.message ?? ''}
                  onFocus={() => setLoginIdInputState({ status: 'default' })}
                />
              </InputLabel>
            </div>
            <div className={styles['input-label']}>
              <InputLabel name="비밀번호" htmlFor="login-password">
                <Input
                  id="login-password"
                  placeholder="비밀번호를 입력해 주세요."
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  status={loginPasswordInputState.status}
                  validationMessage={loginPasswordInputState.message ?? ''}
                  onFocus={() => setLoginPasswordInputState({ status: 'default' })}
                />
              </InputLabel>
            </div>
            <div className={styles['checkbox-label']}>
              <Checkbox
                checked={isRememberIdChecked}
                onChange={(e) => setIsRememberIdChecked(e.target.checked)}
                label="아이디 저장"
                size="medium"
              />
            </div>
            <Button color="primary" full className={styles['login-button']} type="submit">
              로그인
            </Button>
          </Form>
          <div className={styles['find']}>
            <TextButton href={PATH.FIND_ID} color="secondary" weight="regular" size="tiny">
              아이디 찾기
            </TextButton>
            <span className={styles['divider']}>|</span>
            <TextButton href={PATH.FIND_PASSWORD} color="secondary" weight="regular" size="tiny">
              비밀번호 찾기
            </TextButton>
          </div>
        </div>
        <div className={styles['terms']}>
          <TextButton size="tiny" weight="regular" color="tertiary" onClick={handleOpenPrivacyPolicy}>
            개인정보 처리방침
          </TextButton>
        </div>
      </div>
      <div className={styles['footer']}>
        <Text color="quaternary" size="xsmall">
          <div className={styles['company-name']}>(주)컬리</div>
          <div className={styles['company-information']}>
            서울시 강남구 테헤란로 133, 18층(역삼동) | 대표이사 : 김슬아 | 사업자등록번호 : 261-81-23567
          </div>
          <div>© KURLY CORP. ALL RIGHTS RESERVED</div>
        </Text>
      </div>
      <Dialog
        open={openDuplicatedLoginDialog}
        type="confirm"
        title="이미 접속중인 아이디입니다."
        cancelText="취소"
        onCancel={() => setOpenDuplicatedLoginDialog(false)}
        onConfirm={() => postLogin(true)}
      >
        로그인 시 기존 사용자는 자동 로그아웃됩니다.
        <br />
        로그인하시겠습니까?
      </Dialog>
      <Dialog
        open={changePasswordDialogState.open}
        title="비밀번호를 변경해주세요."
        onConfirm={handleClickCloseFirstLoginDialog}
      >
        {changePasswordDialogState.content ?? ''}
      </Dialog>
    </div>
  );
};

export default LoginPage;
